import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} productLink - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;
  /** @type {HTMLElement | null} */
  #sectionHoverTarget = null;
  /** @type {((event: PointerEvent) => void) | null} */
  #sectionPointerenterHandler = null;
  #dialogRequestId = 0;
  #hoverRegionVisited = false;
  timer = /** @type {number | null} */ (null);
  closeTimer = /** @type {number | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    // Set up initial event listeners based on current breakpoint
    this.#handleBreakpointChange();
    this.#setupSectionHoverListener();

    // Listen for breakpoint changes
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up listeners
    this.#removeDesktopListeners();
    this.#removeSectionHoverListener();
    this.#clearCloseTimer();
    document.removeEventListener('pointermove', this.#handleOpenDialogPointerMove);
    this.#hoverRegionVisited = false;
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Open a configured default hotspot when a pointer enters the image area.
   * Touch devices keep their native tap-to-open behavior.
   * @returns {void}
   */
  #setupSectionHoverListener() {
    if (this.dataset.autoOpenOnSectionHover !== 'true') return;

    const sectionContent = this.closest('.section-product-hotspots__content');
    if (!(sectionContent instanceof HTMLElement)) return;

    /** @param {PointerEvent} event */
    const pointerenterHandler = (event) => {
      if (isMobileBreakpoint() || event.pointerType === 'touch') return;
      this.showDialog();
    };

    this.#sectionHoverTarget = sectionContent;
    this.#sectionPointerenterHandler = pointerenterHandler;
    sectionContent.addEventListener('pointerenter', pointerenterHandler);
  }

  /** @returns {void} */
  #removeSectionHoverListener() {
    if (this.#sectionHoverTarget && this.#sectionPointerenterHandler) {
      this.#sectionHoverTarget.removeEventListener('pointerenter', this.#sectionPointerenterHandler);
    }

    this.#sectionHoverTarget = null;
    this.#sectionPointerenterHandler = null;
  }

  /**
   * Open the quick-add modal
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));

    if (!quickAddComponent) return;
    quickAddComponent.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      this.#hoverRegionVisited = true;
      this.#clearCloseTimer();
      if (dialog.open) return;

      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      // Add pointerleave listener when entering trigger
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  /**
   * Remove desktop event listeners from trigger
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    // Clear any pending timer
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    this.#clearCloseTimer();
  }

  /** @returns {void} */
  #clearCloseTimer() {
    if (!this.closeTimer) return;

    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  /** @returns {void} */
  #scheduleDialogClose() {
    this.#clearCloseTimer();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.closeDialog();
    }, 140);
  }

  /** @returns {void} */
  #handleHoverRegionEnter = () => {
    this.#hoverRegionVisited = true;
    this.#clearCloseTimer();
  };

  /**
   * Close only after the pointer has visited the trigger or product card and
   * then moves outside both. This avoids relying on dialog pointerleave,
   * which is inconsistent when an overlay link covers the dialog contents.
   * @param {PointerEvent} event
   * @returns {void}
   */
  #handleOpenDialogPointerMove = (event) => {
    if (!this.#hoverRegionVisited) return;

    const { dialog, trigger } = this.refs;
    const target = event.target;
    const isInsideHoverRegion =
      target instanceof Node && (trigger.contains(target) || dialog.contains(target));

    if (isInsideHoverRegion) {
      this.#clearCloseTimer();
      return;
    }

    if (!this.closeTimer) this.#scheduleDialogClose();
  };

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    // Remove existing listeners
    this.#removeDesktopListeners();

    // Set up desktop hover listeners only (mobile uses on:click in template)
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    // Spacing constants
    const BUTTON_GAP = 10; // Gap between button and dialog
    const CONTAINER_GAP = 10; // Gap from container edges
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    // Get container bounds
    const containerRect = hotspotsContainer?.getBoundingClientRect();

    // Get button dimensions
    const triggerRect = trigger.getBoundingClientRect();

    // To get dialog dimensions, we need to temporarily show it invisibly
    // Show dialog invisibly to measure it
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    // Reset dialog state
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');
    // Calculate button position relative to container
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    // Calculate available space
    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    // Determine horizontal placement
    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    // Determine vertical placement
    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop; // Default to top-aligned
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        // If top-aligned overflows bottom
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          // If bottom-aligned overflows top
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          // If bottom-aligned overflows bottom
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          // If top-aligned overflows top
          if (dialogStartY < CONTAINER_GAP) {
            verticalOffset = CONTAINER_GAP - dialogStartY;
          }
          y = 'bottom';
        }
      }
    } else {
      // For center horizontal: position below or above button
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        // If neither fits well, choose based on button position
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    // Set placement data attribute
    dialog.dataset.placement = `${x},${y}`;

    // Apply vertical offset if needed to keep dialog in bounds
    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    // Return a promise that resolves after a few ticks to ensure styles are applied
    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Handle pointer leave.
   * @param {PointerEvent} e - The event.
   * @returns {void}
   */
  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    // Clear open timer if leaving trigger before dialog opens
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const nextTarget = e.relatedTarget;
    const isMovingWithinHotspot =
      nextTarget instanceof Node && (trigger.contains(nextTarget) || dialog.contains(nextTarget));

    if (isMovingWithinHotspot) {
      this.#clearCloseTimer();
      return;
    }

    // The dialog is visually separated from the trigger by a small gap.
    // A short grace period lets the pointer cross that gap without collapsing it.
    this.#scheduleDialogClose();
  };

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click - on mobile/touch devices opens quick-add, on desktop opens dialog
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    // Check if it's a touch device (tablets) or mobile breakpoint
    if (isMobileBreakpoint() || isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      this.#openQuickAddModal();
    } else {
      this.showDialog();
    }
  };

  showDialog = async () => {
    const { dialog } = this.refs;

    if (dialog.open || dialog.dataset.showing === 'true') return;
    const requestId = ++this.#dialogRequestId;
    dialog.dataset.showing = 'true';

    const hotspotsContainer = this.closest('.hotspots-container');
    if (hotspotsContainer) {
      const openSiblings = Array.from(
        hotspotsContainer.querySelectorAll('product-hotspot-component')
      ).filter(
        (hotspot) =>
          hotspot instanceof ProductHotspotComponent &&
          hotspot !== this &&
          (hotspot.refs.dialog.open || hotspot.refs.dialog.dataset.showing === 'true')
      );

      await Promise.all(openSiblings.map((hotspot) => hotspot.closeDialog()));
    }

    await this.#calculateDialogPlacement();
    if (requestId !== this.#dialogRequestId) return;

    dialog.show();
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
    // Treat the trigger and dialog as one hover region.
    dialog.addEventListener('pointerenter', this.#handleHoverRegionEnter);
    document.addEventListener('pointermove', this.#handleOpenDialogPointerMove);
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    this.#dialogRequestId += 1;
    this.#clearCloseTimer();
    document.removeEventListener('pointermove', this.#handleOpenDialogPointerMove);
    this.#hoverRegionVisited = false;

    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
      return;
    }

    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    // Remove hover-region listeners when closing
    dialog.removeEventListener('pointerenter', this.#handleHoverRegionEnter);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    // we need to use a data-attribute to keep transition-behavior working only when open
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

// Register custom element
customElements.define('product-hotspot-component', ProductHotspotComponent);
